import React from 'react'
import PropTypes from 'prop-types'

import { Box, Container, Typography } from '@material-ui/core'

import Faqs from 'artkive/components/Faqs'
import { TrackLink } from 'artkive/components/Tracker'
import usePageScrollTracker from 'artkive/components/Tracker/usePageScrollTracker'
import { GET_BOX_CLICK, REVIEWS_PAGE } from 'artkive/constants/tracker/mainNavigation'
import withApp from 'artkive/hoc/withApp'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import useSale from 'artkive/hooks/useSale'
import theme from 'artkive/utils/theme'

import GetStartedFooter from '../Home/GetStartedFooter'

import Highlights from './Highlights'
import { Cta, Hero, HeroContainer, PageRoot, RatingRow, ReviewsWrapper, StarRating, SummaryCard } from './Reviews.style'
import StaticReviews from './StaticReviews'
import useReviewsWidget, { WIDGET_ID } from './useReviewsWidget'

const toFaqItem = ({ question, answer, quote, quoteAuthor }, index) => ({
  id: index + 1,
  title: question,
  // MarkdownText only renders strong, links and lists, so the quote stays plain text and only the author is bold.
  body: `${answer}\n\n“${quote}” — **${quoteAuthor}**`,
})

// Server-rendered so the aggregate line, the reviews and the FAQ are in the initial HTML for search and AI crawlers
const Reviews = ({ rating, summary, highlights, faqs, reviews }) => {
  usePageScrollTracker()
  const isMobile = useMobileBreakpoint()
  const widgetReady = useReviewsWidget()
  const { boxCheckoutUrl } = useSale()

  return (
    <PageRoot>
      <Hero>
        <HeroContainer>
          <Typography variant='h1' align='center'>
            Artkive Reviews
          </Typography>
        </HeroContainer>
      </Hero>

      <Container>
        <SummaryCard elevation={0}>
          <RatingRow>
            <StarRating value={Number(rating.value)} readOnly aria-label={`${rating.value} out of 5 stars`} />
            <Typography variant='subtitle1' component='p' align='center'>
              {summary}
            </Typography>
          </RatingRow>
          <Cta align='center'>
            <TrackLink
              href={boxCheckoutUrl}
              trackerEvent={GET_BOX_CLICK}
              trackerOptions={{ section: 'reviews_summary' }}
            >
              Turn your kids’ art into a book →
            </TrackLink>
          </Cta>
          <Highlights {...highlights} />
        </SummaryCard>

        <Box pt={isMobile ? 8 : 12} pb={isMobile ? theme.spacings.sectionMobile : theme.spacings.section}>
          <Box mb={isMobile ? 4 : 6}>
            <Typography variant='h2' align='center'>
              What Parents Are Saying
            </Typography>
          </Box>
          <ReviewsWrapper>
            {reviews.length > 0 && (
              <section
                id='ssr-reviews'
                aria-label='Customer reviews'
                style={{ display: widgetReady ? 'none' : 'block' }}
              >
                <StaticReviews reviews={reviews} />
              </section>
            )}
            <div id={WIDGET_ID} />
          </ReviewsWrapper>
        </Box>
      </Container>

      <Faqs faqs={faqs.map(toFaqItem)} bgcolor='white' trackerEvent={REVIEWS_PAGE.FAQ_ITEM_OPEN} />

      <GetStartedFooter />
    </PageRoot>
  )
}

Reviews.propTypes = {
  rating: PropTypes.shape({
    value: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
  }).isRequired,
  summary: PropTypes.string.isRequired,
  highlights: PropTypes.object.isRequired,
  faqs: PropTypes.arrayOf(PropTypes.object).isRequired,
  reviews: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default withApp(Reviews)
